"use client";

import Image from "next/image";
import { Heading, Text } from "@/components/ui/typography";

type Props = {
  title?: string;
  subtitle?: string;
  primaryCtaText?: string;
  primaryCtaHref?: string;
  secondaryCtaText?: string;
  secondaryCtaHref?: string;
};

const DEFAULTS = {
  title: "How MuteTaxes Sets Up Your HK Tax Structure in 14 Days",
  subtitle:
    "From your first free call to a fully operational Hong Kong company with 0% offshore tax status, here is exactly what happens, what you do, and what we handle.",
  primaryCtaText: "Book Your Free Strategy Call",
  primaryCtaHref:
    "https://calendly.com/d/cxy2-dy2-tqk/mutetaxes-consultation-call",
  secondaryCtaText: "Chat on WhatsApp",
  secondaryCtaHref:
    "https://api.whatsapp.com/send?phone=447878491102&text=Hi%20MuteTaxes%20team!%20I%27m%20interested%20in%20saving%20taxes%20and%20would%20like%20to%20know%20more",
};

export default function HeroSection({
  title = DEFAULTS.title,
  subtitle = DEFAULTS.subtitle,
  primaryCtaText = DEFAULTS.primaryCtaText,
  primaryCtaHref = DEFAULTS.primaryCtaHref,
  secondaryCtaText = DEFAULTS.secondaryCtaText,
  secondaryCtaHref = DEFAULTS.secondaryCtaHref,
}: Props = {}) {
  return (
    <section className="w-full min-[1340px]:px-0 px-4 pt-[120px] pb-[120px] relative overflow-hidden">
      <div className="max-w-[1200px] mx-auto flex flex-col lg:flex-row items-center gap-8">
        {/* LEFT CONTENT */}
        <div className="flex-1 flex flex-col gap-8">
          {/* Heading */}
          <div className="flex flex-col gap-6">
            <Heading as="h1" variant="hero" className="text-left">
              {title}
            </Heading>

            <Text variant="muted" className="sm:text-[18px] font-medium">
              {subtitle}
            </Text>
          </div>

          {/* Buttons */}
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4 mt-2">
              {/* Primary */}
              <a
                href={primaryCtaHref}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 w-fit bg-[#335CFF] text-white px-5 py-2.5 rounded-lg text-sm font-normal hover:opacity-90 transition"
              >
                {primaryCtaText}
                <span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.7965 9.99932L7.08398 6.28682L8.14448 5.22632L12.9175 9.99932L8.14448 14.7723L7.08398 13.7118L10.7965 9.99932Z"
                      fill="white"
                    />
                  </svg>
                </span>
              </a>

              {/* Secondary */}
              <a
                href={secondaryCtaHref}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center w-fit gap-2 border border-[#6fa6ff] text-[#6fa6ff] bg-transparent px-5 py-2.5 rounded-lg text-sm font-normal hover:bg-white/5 hover:border-transparent transition"
              >
                {secondaryCtaText}
                <span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.7965 9.99932L7.08398 6.28682L8.14448 5.22632L12.9175 9.99932L8.14448 14.7723L7.08398 13.7118L10.7965 9.99932Z"
                      fill="#6fa6ff"
                    />
                  </svg>
                </span>
              </a>
            </div>
            <Text variant="muted" muted={60} size="xs">
              No obligation | 30-minute consultation | Tailored to your
              situation
            </Text>
          </div>

          {/* Social proof */}
          <div className="flex flex-col items-start gap-3">
            {/* AVATARS */}
            <div className="flex items-center gap-3 shadow-[20px_40px_80px_0_rgba(45,51,81,0.12)] border border-white/10 px-3 py-2 rounded-full">
              <div className="flex -space-x-2">
                <Image
                  src="/avatar-1.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="rounded-full border"
                />
                <Image
                  src="/avatar-2.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="rounded-full border"
                />
                <Image
                  src="/avatar-3.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="rounded-full border"
                />
              </div>

              <span className="text-xs sm:text-sm text-white/60">
                Trusted by international founders restructuring through Hong
                Kong
              </span>
            </div>

            {/* RATING */}
            <div className="flex items-center gap-2 flex-wrap">
              <Image src="/image-32.webp" alt="logo" width={48} height={16} />

              <div className="flex items-center gap-1 text-sm text-white/60">
                {[1, 2, 3, 4].map((i) => (
                  <Image
                    key={i}
                    src="/star-s-fill.svg"
                    alt="star"
                    width={16}
                    height={16}
                  />
                ))}

                {/* half star */}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8.00092 9.59365L9.69112 10.6256L9.23152 8.69965L10.7357 7.41145L8.76172 7.25305L8.00092 5.42485V9.59365ZM8.00092 11L4.47412 13.154L5.43292 9.13405L2.29492 6.44605L6.41392 6.11605L8.00092 2.30005L9.58792 6.11605L13.7075 6.44605L10.5689 9.13405L11.5277 13.154L8.00092 11Z"
                    fill="#FDC700"
                  />
                </svg>

                <span className="ml-1">4.5/5.0</span>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT IMAGE */}
        <div className="flex-1 relative w-full min-h-[320px] sm:min-h-[380px] lg:min-h-[506px]">
          <div className="relative w-full h-[350px] sm:h-[380px] lg:h-[506px] rounded-[30px] overflow-hidden">
            <Image
              src="/how-it-work/how-it-work-men.webp"
              alt="CTA"
              fill
              priority
              className="object-cover"
            />
          </div>

          {/* floating image */}
          <Image
            src="/hero-2.webp"
            alt="floating"
            width={120}
            height={120}
            className="md:block hidden absolute bottom-[-20px] right-[-20px] sm:bottom-[-30px] sm:right-[-40px] lg:right-[-60px]"
          />
          <Image
            src="/hero-2.webp"
            alt="floating"
            width={120}
            height={120}
            className="md:hidden block absolute top-[10px] right-[-10px] sm:bottom-[-30px] sm:right-[-40px] lg:right-[-60px]"
          />
        </div>
      </div>
    </section>
  );
}
